"use client";

import { useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cx } from "@/lib/utils/cx";
import type { Locale } from "@/i18n/config";
import { useI18n } from "@/i18n/I18nProvider";
import { localizedText } from "@/i18n/text";
import { useListingDocuments } from "@/features/documents/hooks/useListingDocuments";
import { useMyDocuments, useUploadDocument } from "@/features/documents/hooks/useDocuments";
import type { ListingRequiredDocument } from "@/features/documents/services/landlord-document-service";
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from "@/lib/upload-limits";
import ApplicantDetailsCompletion, {
  applicantCompletionIntro,
} from "@/features/students/components/ApplicantDetailsCompletion";

/** What the student chose for one requirement, sent with the application. */
export type SelectedDocument = {
  documentRequestTypeId: string;
  filesystemId: string;
};

/**
 * The applicant details the landlord still lacks, shown as a step of its own
 * before the documents. Null when the account already holds everything.
 */
export type ApplyCompletionStep = {
  fieldNames: string[];
  companyName?: string | null;
  onCompleted: () => void;
};

/**
 * The dialog shown when applying: everything the advert's requirements profile
 * asks for, and the student's own document library to satisfy it from.
 *
 * <p>The student's profile is a library, not a per-landlord filing cabinet.
 * Choosing here is what connects a document to a landlord, and it happens at the
 * moment of applying — which is also when the applicant record is created, so
 * nothing is filed anywhere until the student actually applies.</p>
 *
 * <p>When the landlord's registry requires details the account does not hold,
 * the dialog opens on a completion step instead and moves on to the documents
 * once they are saved. The document choices live here rather than in the step,
 * so a PROFILE_INCOMPLETE answer mid-flow can swap the step in without losing
 * what the student had already picked.</p>
 */
export function ApplyDocumentsDialog({
  listingId,
  open,
  onCancel,
  onSubmit,
  submitting,
  completion = null,
}: {
  listingId: string;
  open: boolean;
  onCancel: () => void;
  onSubmit: (documents: SelectedDocument[]) => void;
  submitting: boolean;
  completion?: ApplyCompletionStep | null;
}) {
  const { locale } = useI18n();
  const { data, isLoading } = useListingDocuments(listingId, open);
  const { data: library } = useMyDocuments();

  /** requirement caption -> chosen library document */
  const [choices, setChoices] = useState<Record<string, string>>({});

  const documents = data?.documents ?? [];

  const unmetMandatory = useMemo(
    () =>
      documents.filter(
        (document) =>
          document.mandatory && !document.satisfied && !choices[document.caption]
      ),
    [documents, choices]
  );

  function choose(caption: string, filesystemId: string | null) {
    setChoices((current) => {
      const next = { ...current };
      if (filesystemId) {
        next[caption] = filesystemId;
      } else {
        delete next[caption];
      }
      return next;
    });
  }

  function submit() {
    const selected: SelectedDocument[] = documents.flatMap((document) => {
      const filesystemId = choices[document.caption];
      if (!filesystemId || !document.documentRequestTypeId) {
        return [];
      }
      return [
        { documentRequestTypeId: document.documentRequestTypeId, filesystemId },
      ];
    });
    onSubmit(selected);
  }

  if (completion) {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          <DialogTitle>
            {localizedText(locale, "Komplettera dina uppgifter", "Complete your details")}
          </DialogTitle>
          <DialogDescription>
            {applicantCompletionIntro(completion.companyName, locale)}
          </DialogDescription>
          <ApplicantDetailsCompletion
            fieldNames={completion.fieldNames}
            companyName={completion.companyName}
            onCompleted={completion.onCompleted}
            onCancel={onCancel}
            showIntro={false}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogTitle>
          {localizedText(locale, "Dokument för din ansökan", "Documents for your application")}
        </DialogTitle>
        <DialogDescription>
          {data?.companyName
            ? localizedText(
                locale,
                `${data.companyName} begär följande för den här bostaden. Välj bland dina sparade dokument eller ladda upp nya.`,
                `${data.companyName} asks for the following for this home. Choose from your saved documents or upload new ones.`
              )
            : localizedText(
                locale,
                "Välj bland dina sparade dokument eller ladda upp nya.",
                "Choose from your saved documents or upload new ones."
              )}
        </DialogDescription>

        {isLoading && (
          <p className="text-sm text-muted-foreground">
            {localizedText(locale, "Hämtar…", "Loading…")}
          </p>
        )}

        {!isLoading && documents.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {localizedText(
              locale,
              "Den här bostaden kräver inga dokument.",
              "This home requires no documents."
            )}
          </p>
        )}

        {data && !data.reachable && (
          <p className="text-sm text-amber-700">
            {localizedText(
              locale,
              "Vi kunde inte kontrollera vad företaget redan har. Du kan välja dokument ändå.",
              "We could not check what the company already holds. You can still choose documents."
            )}
          </p>
        )}

        <ul className="divide-y">
          {documents.map((document) => (
            <RequirementRow
              key={document.caption}
              document={document}
              locale={locale}
              library={library ?? []}
              chosen={choices[document.caption] ?? null}
              onChoose={(filesystemId) => choose(document.caption, filesystemId)}
            />
          ))}
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <p className="text-sm text-muted-foreground">
            {unmetMandatory.length > 0
              ? localizedText(
                  locale,
                  `${unmetMandatory.length} obligatoriskt dokument saknas.`,
                  `${unmetMandatory.length} required document(s) missing.`
                )
              : localizedText(locale, "Allt obligatoriskt är klart.", "All required documents are ready.")}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} disabled={submitting}>
              {localizedText(locale, "Avbryt", "Cancel")}
            </Button>
            <Button onClick={submit} disabled={submitting || unmetMandatory.length > 0}>
              {submitting
                ? localizedText(locale, "Skickar…", "Sending…")
                : localizedText(locale, "Skicka ansökan", "Send application")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequirementRow({
  document,
  locale,
  library,
  chosen,
  onChoose,
}: {
  document: ListingRequiredDocument;
  locale: Locale;
  library: Array<{ name: string; filesystemId?: string; expired?: boolean }>;
  chosen: string | null;
  onChoose: (filesystemId: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadDocument();

  const accept =
    document.allowedTypes.length > 0
      ? document.allowedTypes
          .map((type) =>
            String(type).toUpperCase() === "PICTURE" ? "image/*" : "application/pdf"
          )
          .join(",")
      : "application/pdf,image/*";

  // A requirement whose caption matches nothing in the company's catalogue has
  // no id to upload against. Shown, but not actionable — hiding it would be
  // worse: the landlord still asks for it.
  const actionable = Boolean(document.documentRequestTypeId);

  function onPick(file: File | undefined) {
    setError(null);
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        localizedText(
          locale,
          `Filen är större än ${MAX_UPLOAD_LABEL}.`,
          `The file is larger than ${MAX_UPLOAD_LABEL}.`
        )
      );
      return;
    }

    // Uploading here adds to the student's library and selects it — the library
    // stays the one place documents live.
    // The library names it after the file; renaming lives on the account page.
    upload.mutate(
      { file },
      {
        onSuccess: (uploaded) => {
          if (uploaded?.filesystemId) {
            onChoose(uploaded.filesystemId);
          }
        },
        onError: () =>
          setError(
            localizedText(
              locale,
              "Dokumentet kunde inte laddas upp. Försök igen.",
              "The document could not be uploaded. Please try again."
            )
          ),
      }
    );
  }

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium">
          {document.caption}{" "}
          <span
            className={cx(
              "text-xs font-normal",
              document.mandatory ? "text-red-700" : "text-muted-foreground"
            )}
          >
            {document.mandatory
              ? localizedText(locale, "Obligatoriskt", "Required")
              : localizedText(locale, "Valfritt", "Optional")}
          </span>
        </p>
        <p className={cx("text-sm", statusTone(document))}>{statusLabel(document, locale)}</p>
      </div>

      {actionable ? (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="min-w-56 rounded-md border px-2 py-1 text-sm"
            value={chosen ?? ""}
            onChange={(event) => onChoose(event.target.value || null)}
          >
            <option value="">
              {document.satisfied
                ? localizedText(locale, "Behåll nuvarande", "Keep current")
                : localizedText(locale, "Välj sparat dokument…", "Choose a saved document…")}
            </option>
            {library
              .filter((item) => item.filesystemId)
              .map((item) => (
                <option key={item.filesystemId} value={item.filesystemId}>
                  {item.name}
                  {item.expired
                    ? localizedText(locale, " (utgånget)", " (expired)")
                    : ""}
                </option>
              ))}
          </select>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(event) => onPick(event.target.files?.[0])}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending
              ? localizedText(locale, "Laddar upp…", "Uploading…")
              : localizedText(locale, "Ladda upp ny", "Upload new")}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-amber-700">
          {localizedText(
            locale,
            "Den här dokumenttypen går inte att ladda upp härifrån. Kontakta bostadsföretaget.",
            "This document type cannot be uploaded from here. Contact the company."
          )}
        </p>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}
    </li>
  );
}

function statusLabel(document: ListingRequiredDocument, locale: Locale) {
  if (document.expired) {
    return localizedText(
      locale,
      `Utgånget ${document.expiresAt ?? ""}`.trim(),
      `Expired ${document.expiresAt ?? ""}`.trim()
    );
  }
  if (document.satisfied) {
    return document.expiresAt
      ? localizedText(
          locale,
          `Inlämnat, giltigt t.o.m. ${document.expiresAt}`,
          `Filed, valid until ${document.expiresAt}`
        )
      : localizedText(locale, "Inlämnat", "Filed");
  }
  return localizedText(locale, "Saknas", "Missing");
}

function statusTone(document: ListingRequiredDocument) {
  if (document.expired) return "text-red-700";
  if (document.satisfied) return "text-muted-foreground";
  return "text-amber-700";
}

export default ApplyDocumentsDialog;
